import { FileText, Loader2, Paperclip, Send, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { MentionTextarea } from './MentionTextarea';
import { toBodyText } from './mentions';
import type { MentionCandidate, MentionPerson, PendingAttachment } from './types';

interface ThreadComposerProps {
	candidates: MentionCandidate[];
	pendingFiles: PendingAttachment[];
	onAddFiles: (files: FileList) => void;
	onRemoveFile: (index: number) => void;
	/** Sends the body with tags already turned into tokens. Resolve true to clear the field. */
	onSend: (body: string) => Promise<boolean>;
	sending: boolean;
	/** Files are being prepared (converted, validated) before they can be sent. */
	preparing?: boolean;
	/** What happens on send, in one line — "Notifies Patrick Vetter's accounts". */
	hint?: ReactNode;
	accept?: string;
	placeholder?: string;
	disabled?: boolean;
}

/**
 * Writing a message: text with an @-picker, files, send. A message needs text
 * or at least one file. Uploading is the app's — it holds the files, gets the
 * upload urls and posts; this only collects and hands over.
 */
export function ThreadComposer({
	candidates,
	pendingFiles,
	onAddFiles,
	onRemoveFile,
	onSend,
	sending,
	preparing = false,
	hint,
	accept,
	placeholder = 'Write a message — type @ to tag someone',
	disabled = false,
}: ThreadComposerProps) {
	const [text, setText] = useState('');
	const [picked, setPicked] = useState<MentionPerson[]>([]);
	const fileRef = useRef<HTMLInputElement>(null);

	const body = toBodyText(text, picked);
	const canSend =
		!disabled && !sending && !preparing && (body.length > 0 || pendingFiles.length > 0);

	const send = async () => {
		if (!canSend) return;
		const ok = await onSend(body);
		if (ok) {
			setText('');
			setPicked([]);
		}
	};

	return (
		<div className="flex flex-col gap-2">
			{pendingFiles.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{pendingFiles.map((f, i) => (
						<span
							key={`${f.name}-${i}`}
							className="flex max-w-56 items-center gap-1.5 rounded-[var(--r-2)] border border-line-1 bg-surface py-1 pr-1 pl-1.5 text-sm text-text-2"
						>
							{f.preview ? (
								<img
									src={f.preview}
									alt=""
									className="size-6 rounded-[var(--r-1)] object-cover"
								/>
							) : (
								<FileText size={14} className="shrink-0 text-text-3" />
							)}
							<span className="truncate">{f.name}</span>
							<button
								type="button"
								aria-label={`Remove ${f.name}`}
								onClick={() => onRemoveFile(i)}
								disabled={sending}
								className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-text-4 hover:bg-surface-hover hover:text-text-1"
							>
								<X size={12} />
							</button>
						</span>
					))}
				</div>
			)}

			<MentionTextarea
				value={text}
				onChange={setText}
				candidates={candidates}
				onMention={(p) => setPicked((prev) => [...prev, p])}
				onSubmit={send}
				placeholder={placeholder}
				disabled={disabled || sending}
			/>

			<div className="flex items-center gap-2">
				<input
					ref={fileRef}
					type="file"
					multiple
					accept={accept}
					className="hidden"
					onChange={(e) => {
						if (e.target.files?.length) onAddFiles(e.target.files);
						e.target.value = '';
					}}
				/>
				<button
					type="button"
					onClick={() => fileRef.current?.click()}
					disabled={disabled || sending || preparing}
					className="flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-sm text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{preparing ? (
						<Loader2 size={14} className="animate-spin" />
					) : (
						<Paperclip size={14} />
					)}
					Attach
				</button>
				<span className="min-w-0 flex-1 truncate text-xs text-text-4">{hint}</span>
				<button
					type="button"
					onClick={send}
					disabled={!canSend}
					className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
				>
					{sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
					Send
				</button>
			</div>
		</div>
	);
}
